import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../hooks/useRole';
import { useReadOnly } from '../hooks/useReadOnly';
import { locationService, customerService } from '../api';
import { Button, Input, ThemedText, Pagination, ActionMenu, ConfirmDialog, SkeletonTable, EmptyState, ErrorState, GenerateDemoDataDialog } from '../components';
import { LocationFormModal } from '../components/LocationFormModal';
import { usePagination } from '../hooks/usePagination';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { SearchContainer, StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { MapPin, Map, Search, ChevronRight, Sparkles } from 'lucide-react';
import { StyledSelect as Select } from '../components/FormStyles';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const ViewToggleContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.xs};
`;

const ViewToggleButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: ${props => props.active ? props.theme.colors.tint : 'transparent'};
  color: ${props => props.active ? props.theme.colors.background : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: ${props => props.active ? '600' : '400'};

  &:hover {
    background-color: ${props => props.active ? props.theme.colors.tint : props.theme.colors.border};
  }
`;

const MapViewContainer = styled.div`
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  height: 600px;
  position: relative;
`;

const InfoWindowContent = styled.div`
  padding: ${props => props.theme.spacing.sm};
  min-width: 200px;
`;

const InfoWindowTitle = styled.h3`
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
`;

const InfoWindowText = styled.p`
  margin: ${props => props.theme.spacing.xs} 0;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const InfoWindowActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
`;

const LocationsPage = () => {
  const theme = useTheme();
  const { isSuperAdmin, isAdmin } = useRole();
  const { user } = useAuth();
  const readOnly = useReadOnly();
  const [isDemoDataOpen, setIsDemoDataOpen] = useState(false);
  // "Generate Demo Data" is for demo-org builders only; read-only viewers would 403 on commit.
  const canGenerateDemoData = user?.isDemoOrg === true && !readOnly;
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState('');

  const {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(20);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [selectedMarker, setSelectedMarker] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    fetchData();
  }, [selectedCustomerFilter, currentPage]);

  // Reset to page 1 on filter change
  useEffect(() => {
    resetPage();
  }, [selectedCustomerFilter, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError(null);

      const params = { ...paginationParams };
      if (selectedCustomerFilter) {
        params.customerId = parseInt(selectedCustomerFilter);
      }

      const locationsResponse = await locationService.getLocations(params);

      const { items: locationsData, totalCount: total } = extractPagedData(locationsResponse);
      setLocations(locationsData);
      setTotalCount(total);

      const customersResponse = await customerService.getCustomers({ pageSize: 1000 });
      const { items: customersData } = extractPagedData(customersResponse);
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setFetchError(error?.response?.data?.message || 'Failed to load locations. Please check your connection and try again.');
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async (id) => {
    try {
      await locationService.deleteLocation(id);
      toast.success('Location deleted successfully');
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete location:', error);
      toast.error(error.response?.data?.message || 'Failed to delete location');
    }
  };

  const openEditModal = (location) => {
    setSelectedLocation(location);
    setIsEditModalOpen(true);
  };

  const filteredLocations = locations.filter(location =>
    location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter locations that have valid coordinates for map view
  const locationsWithCoordinates = filteredLocations.filter(
    location => location.latitude != null && location.longitude != null
  );

  // Calculate map center based on all locations
  const getMapCenter = () => {
    if (locationsWithCoordinates.length === 0) {
      return { lat: 43.65, lng: -79.38 }; // Default to Toronto
    }

    const avgLat = locationsWithCoordinates.reduce((sum, loc) => sum + parseFloat(loc.latitude), 0) / locationsWithCoordinates.length;
    const avgLng = locationsWithCoordinates.reduce((sum, loc) => sum + parseFloat(loc.longitude), 0) / locationsWithCoordinates.length;

    return { lat: avgLat, lng: avgLng };
  };

  const fitMapBounds = useCallback(() => {
    if (!mapRef.current || !window.google || locationsWithCoordinates.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    locationsWithCoordinates.forEach(location => {
      bounds.extend({
        lat: parseFloat(location.latitude),
        lng: parseFloat(location.longitude)
      });
    });

    // Add padding to the bounds
    const padding = { top: 50, right: 50, bottom: 50, left: 50 };
    mapRef.current.fitBounds(bounds, padding);
  }, [locationsWithCoordinates]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Fit bounds when map loads or locations change
  useEffect(() => {
    if (mapRef.current && viewMode === 'map' && locationsWithCoordinates.length > 0 && isLoaded) {
      // Small delay to ensure map is fully rendered
      const timeoutId = setTimeout(() => {
        fitMapBounds();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [locationsWithCoordinates, viewMode, fitMapBounds, isLoaded]);

  const handleMarkerClick = (location) => {
    setSelectedMarker(location);
  };

  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };

  return (
    <Layout currentPath="/locations">
      <PageContainer theme={theme} style={{ maxWidth: '100%' }}>
        <PageHeader theme={theme} style={{ alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#43474f', fontWeight: 500, marginBottom: 10 }}>
              <span>Operations</span>
              <ChevronRight size={12} />
              <span style={{ color: '#232562', fontWeight: 700 }}>Locations</span>
            </div>
            <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1.875rem', fontWeight: 800, color: '#232562', margin: '0 0 6px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Locations</h1>
            <p style={{ color: '#43474f', fontSize: '0.875rem', margin: 0 }}>Manage locations</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canGenerateDemoData && (
              <Button
                variant="secondary"
                onClick={() => setIsDemoDataOpen(true)}
                title="Generate realistic demo locations"
              >
                <Sparkles size={14} /> Generate Demo Data
              </Button>
            )}
            <Button onClick={() => setIsCreateModalOpen(true)}>
              + New Location
            </Button>
          </div>
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer theme={theme}>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search by name, address, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>
          {customers.length > 0 && (
            <Select
              value={selectedCustomerFilter}
              onChange={(e) => setSelectedCustomerFilter(e.target.value)}
              theme={theme}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>{customer.firstName} {customer.lastName}{customer.company ? ` (${customer.company})` : ''}</option>
              ))}
            </Select>
          )}
          <ViewToggleContainer theme={theme}>
            <ViewToggleButton
              theme={theme}
              active={viewMode === 'list'}
              onClick={() => setViewMode('list')}
            >
              List View
            </ViewToggleButton>
            <ViewToggleButton
              theme={theme}
              active={viewMode === 'map'}
              onClick={() => setViewMode('map')}
            >
              Map View
            </ViewToggleButton>
          </ViewToggleContainer>
        </SearchContainer>

        {viewMode === 'list' ? (
          <TableContainer theme={theme}>
            {loading ? (
              <SkeletonTable rows={5} cols={5} theme={theme} />
            ) : fetchError ? (
              <ErrorState
                theme={theme}
                title="Failed to load locations"
                message={fetchError}
                onRetry={fetchData}
              />
            ) : filteredLocations.length === 0 ? (
              <EmptyState
                theme={theme}
                icon={<MapPin size={48} />}
                title="No locations found"
                description={searchTerm ? 'Try adjusting your search term' : 'Add your first location to get started'}
              />
            ) : (
              <Table>
                <Thead theme={theme}>
                  <tr>
                    <Th theme={theme}>Name</Th>
                    <Th theme={theme}>Address</Th>
                    <Th theme={theme}>City</Th>
                    <Th theme={theme}>State</Th>
                    <Th theme={theme}>Zip</Th>
                    <Th theme={theme}>Country</Th>
                    <Th theme={theme}>Customer</Th>
                    <Th theme={theme} style={{ width: '50px' }}></Th>
                  </tr>
                </Thead>
                <Tbody>
                  {filteredLocations.map(location => (
                    <Tr key={location.id} theme={theme}>
                      <Td theme={theme}>
                        <ThemedText type="defaultSemiBold">{location.name}</ThemedText>
                      </Td>
                      <Td theme={theme}>
                        <ThemedText type="small">{location.address || '-'}</ThemedText>
                      </Td>
                      <Td theme={theme}>
                        <ThemedText type="small">{location.city || '-'}</ThemedText>
                      </Td>
                      <Td theme={theme}>
                        <ThemedText type="small">{location.state || '-'}</ThemedText>
                      </Td>
                      <Td theme={theme}>
                        <ThemedText type="small">{location.zipCode || '-'}</ThemedText>
                      </Td>
                      <Td theme={theme}>
                        <ThemedText type="small">{location.country || '-'}</ThemedText>
                      </Td>
                      <Td theme={theme}>
                        <ThemedText type="small">
                          {(() => { const c = customers.find(c => c.id === location.customerId); return c ? `${c.firstName} ${c.lastName}` : '-'; })()}
                        </ThemedText>
                      </Td>
                      <Td theme={theme}>
                        <ActionMenu actions={[
                          { label: 'Edit', onClick: () => openEditModal(location) },
                          { label: 'Delete', onClick: () => setDeleteConfirmId(location.id), variant: 'danger' },
                        ]} />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </TableContainer>
        ) : (
          <MapViewContainer theme={theme}>
            {loading || !isLoaded ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <ThemedText style={{ color: theme.colors.muted }}>Loading map…</ThemedText>
              </div>
            ) : locationsWithCoordinates.length === 0 ? (
              <EmptyState
                theme={theme}
                icon={<Map size={48} />}
                title="No locations with coordinates"
                description="Add coordinates to your locations to see them on the map"
              />
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={getMapCenter()}
                zoom={10}
                onLoad={onMapLoad}
              >
                {locationsWithCoordinates.map(location => (
                  <Marker
                    key={location.id}
                    position={{
                      lat: parseFloat(location.latitude),
                      lng: parseFloat(location.longitude)
                    }}
                    onClick={() => handleMarkerClick(location)}
                    title={location.name}
                  />
                ))}

                {selectedMarker && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedMarker.latitude),
                      lng: parseFloat(selectedMarker.longitude)
                    }}
                    onCloseClick={handleInfoWindowClose}
                  >
                    <InfoWindowContent theme={theme}>
                      <InfoWindowTitle theme={theme}>{selectedMarker.name}</InfoWindowTitle>
                      <InfoWindowText theme={theme}>
                        {selectedMarker.address}
                      </InfoWindowText>
                      <InfoWindowText theme={theme}>
                        {selectedMarker.city}, {selectedMarker.state} {selectedMarker.zipCode}
                      </InfoWindowText>
                      <InfoWindowText theme={theme}>
                        {selectedMarker.country}
                      </InfoWindowText>
                      {selectedMarker.customerId && (
                        <InfoWindowText theme={theme}>
                          Customer: {(() => {
                            const c = customers.find(c => c.id === selectedMarker.customerId);
                            return c ? `${c.firstName} ${c.lastName}` : '-';
                          })()}
                        </InfoWindowText>
                      )}
                      <InfoWindowActions theme={theme}>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            handleInfoWindowClose();
                            openEditModal(selectedMarker);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            handleInfoWindowClose();
                            setDeleteConfirmId(selectedMarker.id);
                            setViewMode('list');
                          }}
                        >
                          Delete
                        </Button>
                      </InfoWindowActions>
                    </InfoWindowContent>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </MapViewContainer>
        )}

        {/* Pagination */}
        {!loading && locations.length > 0 && viewMode === 'list' && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={goToPage}
            itemLabel="locations"
          />
        )}

        <LocationFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchData();
          }}
        />

        <LocationFormModal
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setSelectedLocation(null); }}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedLocation(null);
            fetchData();
          }}
          editLocation={selectedLocation}
        />

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Location"
          message="Are you sure you want to delete this location? This action cannot be undone."
          onConfirm={() => handleDeleteLocation(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />

        {canGenerateDemoData && (
          <GenerateDemoDataDialog
            entityType="locations"
            targetId={user?.organizationId}
            entityName="Locations"
            entityNoun="locations"
            open={isDemoDataOpen}
            onClose={() => setIsDemoDataOpen(false)}
            onCommitted={() => { setIsDemoDataOpen(false); window.location.reload(); }}
          />
        )}
      </PageContainer>
    </Layout>
  );
};

export default LocationsPage;
export { LocationsPage };
